import { useCallback, useEffect, useRef, useState } from 'react';
import { CameraOff, RefreshCw } from 'lucide-react';
import { useApi } from '../api/ApiProvider';
import { errorMessage } from './States';
import { TimeAgo } from './TimeAgo';

interface Props {
  tenantId: string;
  deviceId: string;
  name: string;
  /** Skip the fetch entirely, e.g. while the camera is offline. */
  unavailable?: string;
}

/**
 * A still frame pulled live from a camera.
 *
 * The image cannot be an ordinary `<img src>`: the endpoint needs the bearer
 * token, so the bytes are fetched here and handed to the tag as an object URL,
 * which this component is then responsible for revoking.
 */
export function CameraSnapshot({ tenantId, deviceId, name, unavailable }: Props) {
  const api = useApi();
  const [src, setSrc] = useState<string | null>(null);
  const [takenAt, setTakenAt] = useState<string | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const urlRef = useRef<string | null>(null);

  const show = useCallback((url: string | null) => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = url;
    setSrc(url);
  }, []);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const blob = await api.getCameraSnapshot(tenantId, deviceId);
      show(URL.createObjectURL(blob));
      setTakenAt(new Date().toISOString());
    } catch (err) {
      setError(err);
      show(null);
    } finally {
      setBusy(false);
    }
  }, [api, tenantId, deviceId, show]);

  useEffect(() => {
    if (!unavailable) void load();
  }, [load, unavailable]);

  // Release the last object URL when the camera page closes.
  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    },
    [],
  );

  const message = unavailable ?? (error ? errorMessage(error) : null);

  return (
    <div className="snapshot">
      <div className="snapshot__frame">
        {src && !message ? (
          <img className="snapshot__image" src={src} alt={`Latest frame from ${name}`} />
        ) : (
          <div className="snapshot__empty">
            <CameraOff size={28} aria-hidden="true" />
            <span>{message ?? (busy ? 'Asking the camera for a frame…' : 'No frame yet')}</span>
          </div>
        )}
      </div>
      <div className="snapshot__bar">
        <span className="small muted">
          {takenAt && !message ? (
            <>
              Taken <TimeAgo iso={takenAt} />
            </>
          ) : (
            'Still images are pulled on demand; the dashboard does not record video.'
          )}
        </span>
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => void load()}
          disabled={busy || Boolean(unavailable)}
        >
          <RefreshCw size={14} aria-hidden="true" /> {busy ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
    </div>
  );
}
